import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import { FinanceFinancialDocumentComplianceExtraFieldsConfig } from 'generated/resources/datatable.graphql'
import { SaleInvoiceComplianceExtraFieldsFieldFragment } from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import {
  complianceExtraFieldsForLineItemId,
  sortedComplianceExtraFieldsForContext,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/complianceExtraFields'
import { SaleInvoiceDocument } from 'modules/finance/lib/salesInvoiceUtils'

import { LineItemRow } from './_LineItemRow'
import LineItemsExtraRows from './_LineItemsExtraRows'

interface LineItemsProps {
  locale: string
  saleInvoice: SaleInvoiceDocument
  countryComplianceExtraFieldsConfig?: FinanceFinancialDocumentComplianceExtraFieldsConfig
}

const LineItemsHeaderCell = ({ value }: { value: string }) => (
  <Text.Normal size="200" weight="medium">
    {value}
  </Text.Normal>
)

const LineItems = ({ locale, saleInvoice, countryComplianceExtraFieldsConfig }: LineItemsProps) => {
  const lineItems = saleInvoice.lineItems || []
  const currency = saleInvoice.currency

  const hasComplianceExtraFields = !!countryComplianceExtraFieldsConfig?.fields?.length

  const lineItemComplianceExtraFields = useUnsafeMemo(() => {
    if (!hasComplianceExtraFields) {
      return []
    }
    return sortedComplianceExtraFieldsForContext(
      countryComplianceExtraFieldsConfig.fields,
      'line_item'
    )
  }, [hasComplianceExtraFields, countryComplianceExtraFieldsConfig])

  const salesInvoiceComplianceExtraFieldsForLineItemId = (
    lineItemId: string
  ): SaleInvoiceComplianceExtraFieldsFieldFragment[] => {
    return complianceExtraFieldsForLineItemId(
      lineItemComplianceExtraFields,
      saleInvoice.complianceFields?.[0]?.fields || [],
      lineItemId
    )
  }

  return (
    <Table fullWidth style={{ overflowY: 'auto', height: 'auto' }} autoLayout>
      <Table.Head>
        <Table.Row>
          <Table.HeadCell paddingX="s4" ellipsis background="grey000White">
            <Box style={{ paddingLeft: '20px' }}>
              <LineItemsHeaderCell
                value={i18n.t('finance.sale_invoices.detail.line_items.description')}
              />
            </Box>
          </Table.HeadCell>
          <Table.HeadCell paddingX="s24" ellipsis background="grey000White">
            <Box style={{ paddingRight: '20px' }}>
              <LineItemsHeaderCell
                value={i18n.t('finance.sale_invoices.form.line_items.quantity_short')}
              />
            </Box>
          </Table.HeadCell>
          <Table.HeadCell paddingX="s24" ellipsis background="grey000White">
            <Box width="full" alignItems="flexEnd" textAlign="right">
              <LineItemsHeaderCell
                value={i18n.t('finance.sale_invoices.detail.line_items.unit_price')}
              />
            </Box>
          </Table.HeadCell>
          <Table.HeadCell paddingX="s24" ellipsis background="grey000White">
            <Box style={{ paddingRight: '20px' }}>
              <LineItemsHeaderCell value={i18n.t('finance.sale_invoices.form.line_items.tax')} />
            </Box>
          </Table.HeadCell>
          <Table.HeadCell paddingX="s24" ellipsis background="grey000White">
            <Box width="full" alignItems="flexEnd">
              <LineItemsHeaderCell
                value={i18n.t('finance.sale_invoices.detail.line_items.amount')}
              />
            </Box>
          </Table.HeadCell>
          {lineItemComplianceExtraFields.map((field) => (
            <Table.HeadCell
              key={field.name}
              paddingX="s24"
              ellipsis
              background="grey000White"
              width={220}
            >
              <Box style={{ paddingRight: '20px' }}>
                <LineItemsHeaderCell value={field.label} />
              </Box>
            </Table.HeadCell>
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {lineItems.map((lineItem) => (
          <LineItemRow
            key={lineItem.id}
            lineItem={lineItem}
            currency={currency}
            locale={locale}
            complianceExtraFields={salesInvoiceComplianceExtraFieldsForLineItemId(lineItem.id)}
          />
        ))}
        {currency && locale && (
          <LineItemsExtraRows
            currency={currency}
            locale={locale}
            lineItems={lineItems}
            totalAmountCents={saleInvoice.totalAmountCents || undefined}
            discountPercentage={saleInvoice.discountPercentage || undefined}
            taxes={saleInvoice.taxes}
            extraEmptyColumns={lineItemComplianceExtraFields.length}
          />
        )}
      </Table.Body>
    </Table>
  )
}

export default LineItems
